/**
 * Warning on the Archive modal, for Movatic admins: the legacy archive cancels
 * every rider on the plan, so when the plan still has live riders, point the
 * operator at "Replace with another plan…" instead.
 *
 * "Live" is exactly what the legacy `cancel_user_membership` touches: no
 * canceled date, no rejected date, not expired. That is approved riders plus
 * every pending one, whether waiting on an admin or on email verification.
 * The count comes from listUserMemberships with a page of one.
 *
 * If the count cannot be read, the warning still shows, in neutral words: a
 * missing warning is the mistake this exists to prevent.
 */

import React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useUserMembershipsRelayQuery } from '../../../hooks/membership/query/V2/useUserMembershipsRelay';

export type LiveRiderCount =
  | { state: 'off' }
  | { state: 'loading' }
  | { state: 'unknown' }
  | { state: 'known'; count: number };

export function useLiveRiderCount(
  membershipId: string | undefined,
  enabled: boolean
): LiveRiderCount {
  const on = enabled && Boolean(membershipId);
  const live = useUserMembershipsRelayQuery(
    { membershipId, notEnded: true },
    { limit: 1, enabled: on }
  );
  if (!on) return { state: 'off' };
  if (live.error) return { state: 'unknown' };
  if (live.count === undefined || live.count === null) {
    return live.isLoading === false && live.isFetched
      ? { state: 'unknown' }
      : { state: 'loading' };
  }
  return { state: 'known', count: live.count };
}

interface ArchiveRidersWarningProps {
  membershipId: string | undefined;
  /** Movatic admin and the Archive modal is open. */
  enabled: boolean;
  onReplaceInstead: () => void;
}

function ArchiveRidersWarning({
  membershipId,
  enabled,
  onReplaceInstead,
}: ArchiveRidersWarningProps) {
  const live = useLiveRiderCount(membershipId, enabled);
  if (live.state === 'off' || live.state === 'loading') return null;
  if (live.state === 'known' && live.count === 0) return null;
  return (
    <Alert
      severity="warning"
      sx={{ mt: 2 }}
      data-id="archive-riders-warning"
      action={
        <Button
          color="inherit"
          size="small"
          onClick={onReplaceInstead}
          sx={{ float: 'none' }}
        >
          Replace instead
        </Button>
      }
    >
      {live.state === 'known'
        ? `Archiving cancels ${
            live.count === 1 ? '1 rider' : `${live.count} riders`
          }. Use Replace to move them to another plan.`
        : 'Could not check for live riders. Archiving cancels everyone on this plan; use Replace if anyone is on it.'}
    </Alert>
  );
}

export default ArchiveRidersWarning;
